/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: TermsAndConditions
 * File     : TermsAndConditions.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the TermsAndConditions view and coordinating data fetching.
 *   Exports: TermsAndConditions
 *   
 *
 * Dependencies:
 *   - @mui/material
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/termsandconditions" element={<TermsAndConditions />} />
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>Terms and Conditions</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>Last updated: 2025</Typography>

      <Typography paragraph>
        By accessing or using ReelConsensus you agree to these Terms. If you do not agree, do not use the service.
      </Typography>

      <Typography variant="h6" gutterBottom>Use of Service</Typography>
      <Typography paragraph>
        You must comply with applicable laws, respect intellectual property, and refrain from abusive or illegal
        activity. We may suspend or terminate accounts that violate these Terms.
      </Typography>

      <Typography variant="h6" gutterBottom>Accounts & Billing</Typography>
      <Typography paragraph>
        You are responsible for safeguarding your account. Paid features are billed via Stripe; subscriptions renew until canceled.
        Host plan includes up to 5 clubs; optional non‑stacking add‑on tiers allow more (Tier 1: 6–9, Tier 2: 10–19, Tier 3: 20–29).
        Downgrades may be blocked if your club count exceeds the selected tier. Refunds are handled per Stripe and our policy.
      </Typography>

      <Typography variant="h6" gutterBottom>Ratings</Typography>
      <Typography paragraph>
        Ratings are available after the winner’s runtime elapses and are limited to users who voted in the poll. Ratings are final once submitted and displayed as part of the poll’s results.
      </Typography>

      <Typography variant="h6" gutterBottom>Analytics & Attribution</Typography>
      <Typography paragraph>
        We may record anonymous referral and UTM information and send UTM metadata to Stripe to understand subscription attribution. See our Privacy Policy for details.
      </Typography>

      <Typography variant="h6" gutterBottom>GDPR/CCPA Data Deletion</Typography>
      <Typography paragraph>
        Upon a verified request or as initiated by an Owner/Admin, we will perform a GDPR wipe of a user which deletes comments, anonymizes votes, reassigns any owned clubs to the platform Owner, reassigns created polls to the Owner, and removes the user account. Certain minimal records may be retained where legally necessary (e.g., billing records with Stripe).
      </Typography>

      <Typography variant="h6" gutterBottom>Content</Typography>
      <Typography paragraph>
        You retain ownership of content you submit and grant us a license to host and display it for service delivery.
        Content that violates laws or these Terms may be removed. Movie metadata shown (OMDb/Watchmode/Plex) is provided by third‑party services. ReelConsensus does not host or stream media; Plex integration is used solely to list titles for creating and voting on polls. Club Streamer uses YouTube&rsquo;s embedded player and does not host or stream content directly. We do not support piracy.
      </Typography>

      <Typography variant="h6" gutterBottom>Pyhsical Media Collection</Typography>
      <Typography paragraph>
        Physical Media Collection features allow you to import and manage your physical movie collection data, including metadata, images, and purchase information. You are responsible for ensuring you have the right to import and store this data. Images and metadata imported from external sources are stored as base64 data within our database. You may delete your collection data at any time through the service interface. We do not claim ownership of your collection data and will not use it for purposes other than providing the service features you request.
      </Typography>

      <Typography variant="h6" gutterBottom>Disclaimers</Typography>
      <Typography paragraph>
        Service is provided &ldquo;as is&rdquo; without warranties. To the extent permitted by law, we disclaim all implied
        warranties and limit liability to amounts paid in the preceding 12 months.
      </Typography>

      <Typography variant="h6" gutterBottom>Contact</Typography>
      <Typography paragraph>
        Questions: info@reelconsensus.com.
      </Typography>
    </Box>
  );
};

export default TermsAndConditions;

